import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { FormAutoSaver } from 'common/helpers/form-auto-saver';
import { useFormState } from 'components/elements/form';
import Popover, { PopoverMenuType } from 'components/popover';
import useApprovalAction from 'hooks/use-approval-action';
import { useAuthorization } from 'hooks/use-authorization';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import { isDataActive } from 'hooks/use-void-action';
import { tourListToActivityPurchase } from 'legacy-modules/order/activity/hooks/use-get-default-values';
import { tourListToCruisePurchase } from 'legacy-modules/order/cruise/hooks/use-get-default-values';
import { tourListToHotelPurchase } from 'legacy-modules/order/hotel/hooks/use-get-default-values';
import { tourListToFlightPurchase } from 'legacy-modules/order/ticket-order/hooks/use-get-default-values';
import { tourListToDocumentPurchase } from 'legacy-modules/order/travel-document/hooks/use-get-default-values';
import { tourListToTourSale } from 'legacy-modules/tour/tour-sale/hooks/use-get-default-values';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import useCancelTourAction from './hooks/use-cancel-tour.action';
import useCloseTourAction from './hooks/use-close-tour-action';
import useStaffCommissionAction from './hooks/use-staff-commission-action';
import useTourExpenseAction from './hooks/use-tour-expense-action';
import useTourIncomeAction from './hooks/use-tour-income-action';
import useTourParticipantAction from './hooks/use-tour-participant-action';
import useTourPurchaseListAction from './hooks/use-tour-purchase-list-action';
import useTourSaleListAction from './hooks/use-tour-sale-list-action';
import useUncloseTourAction from './hooks/use-unclose-tour-action';
import { TourListFormType } from '../form-type';

interface Props {
  tour: TourModel;
  defaultValues: TourListFormType;
}

export default function TourActionComponent(props: Props) {
  const { tour, defaultValues } = props;
  const permission = useGetCrudPermission('tours');
  const { t } = useTranslation();
  const status = tour.status;
  const isActive = isDataActive(status);
  const id = tour.id;
  const { setIsEditable } = useFormState();
  const { navigate } = useNavigation();
  const { can } = useAuthorization();
  const onEdit = React.useCallback(() => {
    setIsEditable(true);
  }, [setIsEditable]);
  const editPermission = permission.update && isActive;
  const editAction: PopoverMenuType = {
    key: `edit-action-${id}`,
    label: t('common:edit_tour'),
    onClick: onEdit,
    isAllowed: editPermission,
  };

  const onNavigateActivityPurchase = () => {
    const defaultValues = tourListToActivityPurchase(tour);
    FormAutoSaver.remove(NavigationRoutes.ActivityPurchasesCreate);
    navigate(NavigationRoutes.ActivityPurchasesCreate, {
      defaultValues,
    });
  };

  const onNavigateCruisePurchase = () => {
    const defaultValues = tourListToCruisePurchase(tour);
    FormAutoSaver.remove(NavigationRoutes.CruisePurchasesCreate);
    navigate(NavigationRoutes.CruisePurchasesCreate, {
      defaultValues,
    });
  };

  const onNavigateHotelPurchase = () => {
    const defaultValues = tourListToHotelPurchase(tour);
    FormAutoSaver.remove(NavigationRoutes.HotelPurchasesCreate);
    navigate(NavigationRoutes.HotelPurchasesCreate, {
      defaultValues,
    });
  };

  const onNavigateFlightPurchase = () => {
    const defaultValues = tourListToFlightPurchase(tour);
    FormAutoSaver.remove(NavigationRoutes.FlightPurchasesCreate);
    navigate(NavigationRoutes.FlightPurchasesCreate, {
      defaultValues,
    });
  };

  const onNavigateDocumentPurchase = () => {
    const defaultValues = tourListToDocumentPurchase(tour);
    FormAutoSaver.remove(NavigationRoutes.DocumentPurchasesCreate);
    navigate(NavigationRoutes.DocumentPurchasesCreate, {
      defaultValues,
    });
  };

  const onNavigateTourSale = () => {
    const defaultValues = tourListToTourSale(tour);
    FormAutoSaver.remove(NavigationRoutes.TourSalesCreate);
    navigate(NavigationRoutes.TourSalesCreate, {
      defaultValues,
    });
  };

  const onDuplicateTourList = () => {
    FormAutoSaver.remove(NavigationRoutes.TourCreate);
    navigate(NavigationRoutes.TourCreate, {
      defaultValues,
    });
  };

  const { revertDraftButton } = useRevertDraftAction('tours', tour);
  const { staffCommissionAction } = useStaffCommissionAction(tour);
  const { tourParticipantAction } = useTourParticipantAction(tour);
  const { tourSaleListAction } = useTourSaleListAction(tour);
  const { tourPurchaseListAction } = useTourPurchaseListAction(tour);
  const { cancelTourAction } = useCancelTourAction(tour);
  const { tourIncomeAction } = useTourIncomeAction(tour);
  const { tourExpenseAction } = useTourExpenseAction(tour);
  const { requestApprovalButton } = useApprovalAction('tours', tour);
  const { closeComponent } = useCloseTourAction(tour);
  const { uncloseComponent } = useUncloseTourAction(tour);
  const actions: PopoverMenuType[] = [
    editAction,
    staffCommissionAction,
    tourParticipantAction,
    tourSaleListAction,
    tourPurchaseListAction,
    {
      key: `duplicate-${id}`,
      label: t('common:duplicate_tour'),
      onClick: onDuplicateTourList,
      isAllowed: permission.create,
    },
    {
      key: `flight-${id}`,
      label: t('common:ticket_order'),
      onClick: onNavigateFlightPurchase,
      isAllowed: can(AuthorizationRules.FlightPurchasesCreate) && isActive,
    },
    {
      key: `hotel-${id}`,
      label: t('common:hotel_purchase'),
      onClick: onNavigateHotelPurchase,
      isAllowed: can(AuthorizationRules.HotelPurchasesCreate) && isActive,
    },
    {
      key: `cruise-${id}`,
      label: t('common:cruise_purchase'),
      onClick: onNavigateCruisePurchase,
      isAllowed: can(AuthorizationRules.CruisePurchasesCreate) && isActive,
    },
    {
      key: `document-${id}`,
      label: t('common:travel_document_purchase'),
      onClick: onNavigateDocumentPurchase,
      isAllowed: can(AuthorizationRules.DocumentPurchasesCreate) && isActive,
    },
    {
      key: `activity-${id}`,
      label: t('common:others_purchase'),
      onClick: onNavigateActivityPurchase,
      isAllowed: can(AuthorizationRules.ActivityPurchasesCreate) && isActive,
    },
    {
      key: `tour-sale-${id}`,
      label: t('common:tour_sale'),
      onClick: onNavigateTourSale,
      isAllowed: can(AuthorizationRules.TourSalesCreate) && isActive,
    },
    tourIncomeAction,
    tourExpenseAction,
    cancelTourAction,
  ].filter((action) => action.isAllowed);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        gap: 16,
      }}
    >
      {revertDraftButton}
      {requestApprovalButton}
      <Popover actions={actions} />
      {closeComponent}
      {uncloseComponent}
    </div>
  );
}
